import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import useScrollToTop from "../../../hooks/useScollToTop";
import { getReglementsByUser } from "../../../services/achatsServices";
import { createCheckoutSessionForArticles } from "../../../services/paymentServices";
import PaymentSuccess from "./PaymentSuccess";
import { getSettings } from "../../../services/settingsServices";
import ArticleTable from "./ArticleTable";
import ArticleCard from "./ArticleCard";
import PaymentPlanModal from "./PaymentPlanModal";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import { sendNotificationToAdmin } from "../../../services/notificationServices";
import { ToastContainer, toast } from "react-toastify";
import formatDate from "../../../utils/formatDate";

import { generateReglementInvoicePdf } from "./../../../dashboard/pages/user/Reglements/utils/invoicePdfUtils";

const NOTIFIED_STORAGE_KEY = "notifiedReglementIds";

const PAYABLE_STATUSES = ["Non Réglé", "En Attente"];

const UsersReglements = () => {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);
  const userId = user?.userId;

  const [reglements, setReglements] = useState([]);
  const [defaultPhotoUrl, setDefaultPhotoUrl] = useState("");
  const [settingsInfo, setSettingsInfo] = useState(null);

  const [maxPayments, setMaxPayments] = useState(1);
  const [paymentInterval, setPaymentInterval] = useState(1);

  const [sessionId, setSessionId] = useState(null);
  const [isPaymentPlanModalOpen, setIsPaymentPlanModalOpen] = useState(false);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [payingId, setPayingId] = useState(null);

  const [notifyingId, setNotifyingId] = useState(null);
  const [manualNotifiedIds, setManualNotifiedIds] = useState([]);

  const notifiedIdsRef = useRef(new Set());
  const prevStatusRef = useRef(new Map());
  const autoNotifyNextLoadRef = useRef(false);
  const autoNotifyLockRef = useRef(false);

  const [autoNotifyEnabled] = useState(true);

  const canUseInstallmentPayments = useMemo(() => {
    const value = Number(maxPayments);
    return Number.isFinite(value) && value > 1;
  }, [maxPayments]);

  useEffect(() => {
    try {
      const saved = JSON.parse(
        localStorage.getItem(NOTIFIED_STORAGE_KEY) || "[]",
      );

      if (Array.isArray(saved)) {
        notifiedIdsRef.current = new Set(saved);
      }
    } catch {}
  }, []);

  const persistNotified = useCallback(() => {
    try {
      localStorage.setItem(
        NOTIFIED_STORAGE_KEY,
        JSON.stringify(Array.from(notifiedIdsRef.current)),
      );
    } catch {}
  }, []);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const settings = await getSettings();

        if (Array.isArray(settings) && settings.length > 0) {
          const s = settings[0];

          setSettingsInfo(s);
          setDefaultPhotoUrl(s.photo_url_default_lic_cot || "");
          const normalizedMaxPayments = Number(s.max_payments);
          const normalizedPaymentInterval = Number(s.payment_interval);

          setMaxPayments(
            Number.isFinite(normalizedMaxPayments) && normalizedMaxPayments > 0
              ? normalizedMaxPayments
              : 1,
          );

          setPaymentInterval(
            Number.isFinite(normalizedPaymentInterval) &&
              normalizedPaymentInterval > 0
              ? normalizedPaymentInterval
              : 1,
          );
        }
      } catch (error) {
        console.error("Erreur lors de la récupération des paramètres", error);
      }
    };

    fetchSettings();
  }, []);

  useEffect(() => {
    const query = new URLSearchParams(window.location.search);
    const sid = query.get("session_id");

    if (sid) {
      setSessionId(sid);
      autoNotifyNextLoadRef.current = true;
    }
  }, []);

  const checkAutoNotifications = useCallback(
    async (freshList) => {
      if (!autoNotifyEnabled || autoNotifyLockRef.current) return;

      if (!autoNotifyNextLoadRef.current) {
        freshList.forEach((reglement) => {
          prevStatusRef.current.set(reglement.id, reglement.statut_reglement);
        });
        return;
      }

      autoNotifyLockRef.current = true;

      try {
        const paid = freshList.filter(
          (reglement) =>
            reglement &&
            reglement.statut_reglement === "Réglé" &&
            !notifiedIdsRef.current.has(reglement.id),
        );

        if (paid.length === 0) return;

        let candidate =
          paid.find(
            (reglement) =>
              reglement.stripe_session_id &&
              reglement.stripe_session_id === sessionId,
          ) ||
          paid.find(
            (reglement) =>
              reglement.session_id && reglement.session_id === sessionId,
          );

        if (!candidate) {
          const recentPaid = paid
            .filter((reglement) =>
              isRecent(reglement.updatedAt || reglement.date_achat, 15),
            )
            .sort(
              (a, b) =>
                new Date(b.updatedAt || b.date_achat) -
                new Date(a.updatedAt || a.date_achat),
            );

          candidate = recentPaid[0];
        }

        if (!candidate) {
          candidate = [...paid].sort(
            (a, b) =>
              new Date(b.updatedAt || b.date_achat) -
              new Date(a.updatedAt || a.date_achat),
          )[0];
        }

        if (candidate) {
          const nomArticle = getArticleName(candidate);

          const message = `${user?.prenom || ""} ${
            user?.nom || ""
          } a effectué un paiement en ligne : ${nomArticle} – ${
            candidate.prix_total
          } € (achat du ${formatDate(candidate.date_achat)}).`;

          await sendNotificationToAdmin(message, user?.token);

          notifiedIdsRef.current.add(candidate.id);
          persistNotified();

          toast.success("Notification envoyée aux administrateurs.");
        }
      } catch (error) {
        console.error("Erreur notification automatique règlement :", error);
        toast.error("Erreur lors de l'envoi de la notification.");
      } finally {
        freshList.forEach((reglement) => {
          prevStatusRef.current.set(reglement.id, reglement.statut_reglement);
        });

        autoNotifyNextLoadRef.current = false;
        autoNotifyLockRef.current = false;
      }
    },
    [autoNotifyEnabled, persistNotified, sessionId, user],
  );

  const loadReglements = useCallback(async () => {
    if (!userId) return;

    try {
      const response = await getReglementsByUser(userId);

      if (!Array.isArray(response)) {
        setReglements([]);
        return;
      }

      const sorted = [...response].sort(
        (a, b) => new Date(b.date_achat) - new Date(a.date_achat),
      );

      const withExtras = sorted.map((reglement) => ({
        ...reglement,
        saison: determineSeason(reglement.date_achat),
        nomArticle: getArticleName(reglement),
        photoUrl:
          reglement?.Article?.photo_url ||
          reglement?.photoUrl ||
          defaultPhotoUrl,
      }));

      await checkAutoNotifications(withExtras);

      setReglements(withExtras);
    } catch (error) {
      console.error("Erreur lors de la récupération des règlements", error);
      toast.error("Impossible de charger vos règlements.");
      setReglements([]);
    }
  }, [userId, defaultPhotoUrl, checkAutoNotifications]);

  useEffect(() => {
    loadReglements();
  }, [loadReglements]);

  useEffect(() => {
    const refreshAfterSuccess = async () => {
      if (!sessionId) return;

      try {
        await loadReglements();
      } finally {
        const url = new URL(window.location.href);
        url.searchParams.delete("session_id");
        window.history.replaceState({}, "", url.toString());
      }
    };

    refreshAfterSuccess();
  }, [sessionId, loadReglements]);

  const nonPaidArticles = useMemo(
    () =>
      reglements.filter((reglement) =>
        PAYABLE_STATUSES.includes(reglement.statut_reglement),
      ),
    [reglements],
  );

  const handlePayerClick = async (reglementId) => {
    if (payingId) return;

    try {
      const reglement = reglements.find((item) => item.id === reglementId);

      if (!reglement) return;
      if (reglement.autorisation_achat === false) return;
      if (!PAYABLE_STATUSES.includes(reglement.statut_reglement)) return;

      setPayingId(reglementId);

      const quantity = Math.max(1, Number(reglement.quantite) || 1);
      const unitPrice = computeUnitPrice(reglement);

      const items = [
        {
          id: reglement.id,
          name: getArticleName(reglement),
          price: unitPrice,
          quantity,
        },
      ];

      const expectedTotal = Number((unitPrice * quantity).toFixed(2));
      const frontTotal = Number((Number(reglement.prix_total) || 0).toFixed(2));

      if (Math.abs(expectedTotal - frontTotal) > 0.02) {
        console.warn(
          "[Paiement] Total recalculé différent du total affiché :",
          {
            expectedTotal,
            frontTotal,
            reglement,
          },
        );
      }

      const url = await createCheckoutSessionForArticles(items);

      if (typeof url === "string" && url.startsWith("http")) {
        window.location.href = url;
      } else {
        toast.error("URL de paiement invalide.");
        console.error("URL de paiement invalide :", url);
      }
    } catch (error) {
      console.error("Erreur création session de paiement :", error);
      toast.error("Impossible de lancer le paiement.");
    } finally {
      setPayingId(null);
    }
  };

  const handlePayerEnPlusieursFoisClick = (reglementId) => {
    if (!canUseInstallmentPayments) {
      toast.info("Le paiement en plusieurs fois n'est pas activé par le club.");
      return;
    }

    const article = reglements.find((item) => item.id === reglementId);

    if (!article) return;
    if (article.article_id !== null) return;
    if (hasEcheanceTag(article.message_achat)) return;
    if (!PAYABLE_STATUSES.includes(article.statut_reglement)) return;
    if (article.autorisation_achat === false) return;

    setSelectedArticle(article);
    setIsPaymentPlanModalOpen(true);
  };

  const handlePaymentPlanSelection = (selectedPlan) => {
    console.log("Paiement en plusieurs fois sélectionné :", selectedPlan);
  };

  const handleNotifyAdmins = async (article) => {
    if (!article || notifyingId) return;

    if (
      notifiedIdsRef.current.has(article.id) ||
      manualNotifiedIds.includes(article.id)
    ) {
      toast.info("Cette notification a déjà été envoyée.");
      return;
    }

    try {
      setNotifyingId(article.id);

      const nomArticle = getArticleName(article);

      const message = `${user?.prenom || ""} ${
        user?.nom || ""
      } a confirmé son paiement en ligne : ${nomArticle} – ${
        article.prix_total
      } € (achat du ${formatDate(article.date_achat)}).`;

      await sendNotificationToAdmin(message, user?.token);

      notifiedIdsRef.current.add(article.id);
      persistNotified();

      setManualNotifiedIds((ids) => [...new Set([...ids, article.id])]);

      toast.success("Notification envoyée aux administrateurs.");
    } catch (error) {
      console.error("Erreur notification admin :", error);
      toast.error("Erreur lors de l'envoi de la notification.");
    } finally {
      setNotifyingId(null);
    }
  };

  const handleGenerateInvoice = useCallback(
    (reglementOrId) => {
      const reglement =
        typeof reglementOrId === "object"
          ? reglementOrId
          : reglements.find((item) => item.id === reglementOrId);

      if (!reglement) {
        toast.error("Impossible de trouver le règlement.");
        return;
      }

      if (!canGenerateInvoice(reglement)) {
        toast.info(
          "La facture est disponible uniquement pour les articles boutique réglés.",
        );
        return;
      }

      try {
        generateReglementInvoicePdf({
          reglement,
          user,
          settings: settingsInfo,
        });

        toast.success("Facture PDF générée.");
      } catch (error) {
        console.error("Erreur génération facture PDF :", error);
        toast.error("Impossible de générer la facture PDF.");
      }
    },
    [reglements, settingsInfo, user],
  );

  const isAlreadyNotified = useCallback(
    (id) => notifiedIdsRef.current.has(id) || manualNotifiedIds.includes(id),
    [manualNotifiedIds],
  );

  return (
    <section className="w-full">
      <div className="pt-[150px]" aria-hidden />

      <div className="mx-auto w-full max-w-7xl px-3 sm:px-6">
        <HeaderTitle
          role="Utilisateur"
          title="Vos Commandes & Règlements"
          align="center"
          className="mb-1"
        />

        <div className="mb-6">
          <DashboardNavigation role={userRole} />
        </div>

        <div className="overflow-hidden rounded-2xl bg-white/80 shadow ring-1 ring-slate-200/70 backdrop-blur dark:bg-slate-900/60 dark:ring-slate-700/70">
          <div className="bg-gradient-to-r from-sky-500 via-violet-500 to-orange-400 px-4 py-5 text-white sm:px-6">
            <div className="flex flex-col gap-2">
              <h4 className="text-lg font-semibold sm:text-xl">Règlements</h4>

              <p className="text-sm text-white/90">
                Réglez en ligne, en toute sécurité, vos articles commandés en
                boutique et vos licences-cotisations.
              </p>

              <p className="text-sm text-white/90">
                Vous avez la possibilité de régler en plusieurs échéances
                uniquement pour les licences-cotisations.
              </p>

              <p className="text-sm text-white/90">
                Si le bouton de paiement n'est pas actif, c'est que le règlement
                a été désactivé par le bureau.
              </p>

              <p className="text-sm text-white/90">
                Une fois le paiement effectué, vous pouvez notifier
                l'administrateur avec le bouton 🔔 Prévenir l'admin.
              </p>

              <p className="text-sm text-white/90">
                Pour les commandes boutique réglées, vous pouvez également
                télécharger une facture acquittée au format PDF.
              </p>
            </div>
          </div>

          <div className="p-4 sm:p-6">
            <div className="mb-4 rounded-xl bg-slate-50 px-4 py-3 text-sm text-slate-600 ring-1 ring-slate-200 dark:bg-slate-800 dark:text-slate-200 dark:ring-slate-700">
              <span className="font-semibold">{reglements.length}</span>{" "}
              règlement{reglements.length > 1 ? "s" : ""} affiché
              {reglements.length > 1 ? "s" : ""}.
              {nonPaidArticles.length > 0 && (
                <>
                  {" "}
                  <span className="font-semibold">
                    {nonPaidArticles.length}
                  </span>{" "}
                  règlement{nonPaidArticles.length > 1 ? "s" : ""} en attente.
                </>
              )}
            </div>

            <div className="w-full border-gray-300 p-2">
              <ArticleTable
                articles={reglements}
                getStatusColor={getStatusColor}
                defaultPhotoUrl={defaultPhotoUrl}
                handlePayerClick={handlePayerClick}
                handlePayerEnPlusieursFoisClick={
                  handlePayerEnPlusieursFoisClick
                }
                canUseInstallmentPayments={canUseInstallmentPayments}
                payingId={payingId}
                handleNotifyClick={handleNotifyAdmins}
                notifyingId={notifyingId}
                isAlreadyNotified={isAlreadyNotified}
                handleGenerateInvoice={handleGenerateInvoice}
              />
            </div>

            <div className="w-full p-2 lg:ml-4">
              <ArticleCard
                articles={reglements}
                getStatusColor={getStatusColor}
                defaultPhotoUrl={defaultPhotoUrl}
                handlePayerClick={handlePayerClick}
                handlePayerEnPlusieursFoisClick={
                  handlePayerEnPlusieursFoisClick
                }
                canUseInstallmentPayments={canUseInstallmentPayments}
                payingId={payingId}
                handleNotifyClick={handleNotifyAdmins}
                notifyingId={notifyingId}
                isAlreadyNotified={isAlreadyNotified}
                handleGenerateInvoice={handleGenerateInvoice}
              />
            </div>
          </div>
        </div>
      </div>

      {isPaymentPlanModalOpen && selectedArticle && canUseInstallmentPayments && (
        <PaymentPlanModal
          isOpen={isPaymentPlanModalOpen}
          onClose={() => setIsPaymentPlanModalOpen(false)}
          totalAmount={selectedArticle.prix_total}
          maxPayments={maxPayments}
          paymentInterval={paymentInterval}
          handlePaymentPlanSelection={handlePaymentPlanSelection}
          article={selectedArticle}
          refreshDataAfterUpdate={loadReglements}
        />
      )}

      {sessionId && <PaymentSuccess sessionId={sessionId} />}

      <ToastContainer />
    </section>
  );
};

export default UsersReglements;

function getStatusColor(status) {
  switch (status) {
    case "Non Réglé":
      return "bg-red-500 text-white";
    case "Réglé":
      return "bg-green-500 text-white";
    case "En Attente":
      return "bg-yellow-300 text-black";
    case "Annulé":
      return "bg-black text-white";
    case "Remboursé":
      return "bg-purple-500 text-white";
    case "Partiellement Réglé":
      return "bg-green-200 text-black";
    default:
      return "bg-gray-200 text-black";
  }
}

function determineSeason(dateString) {
  const date = new Date(dateString);

  if (Number.isNaN(date.getTime())) {
    return "Saison inconnue";
  }

  const year = date.getFullYear();
  const seasonStart = new Date(`${year}-08-01`);
  const seasonEnd = new Date(`${year + 1}-07-31`);

  if (date >= seasonStart && date <= seasonEnd) {
    return `Saison ${year}-${year + 1}`;
  }

  return `Saison ${year - 1}-${year}`;
}

function isRecent(dateString, minutes = 15) {
  if (!dateString) return false;

  const time = new Date(dateString).getTime();

  if (Number.isNaN(time)) return false;

  return Date.now() - time <= minutes * 60 * 1000;
}

function toNumberSafe(value) {
  const number = Number(value);
  return Number.isFinite(number) ? number : 0;
}

function computeUnitPrice(reglement) {
  const quantity = Math.max(1, toNumberSafe(reglement?.quantite));
  const total = toNumberSafe(reglement?.prix_total);
  const unitFromReglement = toNumberSafe(reglement?.prix_unitaire);
  const unitFromArticle = toNumberSafe(reglement?.Article?.prix);

  const unit =
    unitFromReglement ||
    unitFromArticle ||
    (quantity ? total / quantity : total);

  return Number(unit.toFixed(2));
}

function getArticleName(reglement) {
  if (reglement?.article_id === null) {
    return "Règlement Licence-Cotisation";
  }

  return reglement?.Article?.nom || reglement?.nomArticle || "Article";
}

function hasEcheanceTag(message = "") {
  const clean = String(message)
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

  return clean.includes("echeance");
}

function canGenerateInvoice(reglement) {
  const isBoutiqueArticle =
    reglement?.article_id !== null && reglement?.article_id !== undefined;

  return isBoutiqueArticle && reglement?.statut_reglement === "Réglé";
}
